// --- Aux functions to create keys
export const createNameKey = (label, value) => ({
  name: {
    label,
    value
  }
});
const searchByFilterKey = (name, autocomplete, operators) => ({
  name,
  autocomplete,
  operators
});
const groupByKey = (label, field) => ({
  label,
  value: [
    {
      field
    }
  ]
});
export const createFilter = (name, op, val) => ({
  name,
  op,
  val
});
export const createLabelName = (label, name) => ({
  label,
  name
});

const createLabelValue = (label, value) => ({
  label,
  value
});

const FILTER_OPERATORS = {
  eq: createNameKey('=', '=='),
  neq: createNameKey('!=', '!='),
  c: createNameKey('contains', 'like'),
  ilike: createNameKey('contains', 'ilike'),
  greaterOrEqual: createNameKey('>=', '>='),
  greater: createNameKey('>', '>'),
  lessOrEqual: createNameKey('<=', '<='),
  less: createNameKey('<', '<'),
  has: createNameKey('has', 'has'),
  any: createNameKey('any', 'any'),
  in: createNameKey('in', 'in'),
  notIn: createNameKey('not in', 'not_in'),
  isNull: createNameKey('is null', 'is_null'),
  isNotNull: createNameKey('is not null', 'is_not_null'),
  and: createNameKey('&', 'and'),
  or: createNameKey('||', 'or'),
  isOneOf: createNameKey('is one of', '=='),
  isNotOneOf: createNameKey('!=', 'eq'),
  notAny: createNameKey('notAny', 'not_any'),
  notOneOf: createNameKey('is not one of', '=='),
  rangeStatusTrace: createNameKey('range', '=='),
  range: createNameKey('range', 'range'),
  isOneOfList: createNameKey('is one of', 'is_one_of'),
  isNotOneOfList: createNameKey('is not one of', 'is_not_one_of')
};

export const {
  eq, neq, like, ilike, greaterOrEqual, greater, lessOrEqual,
  less, has, any, notIn, isNull, isNotNull, and, or, isOneOf,
  isNotOneOf, notAny, rangeStatusTrace, notOneOf, range, isOneOfList, isNotOneOfList
} = FILTER_OPERATORS;

// --- Risk bands (canonical source of truth — must match backend RiskSchema.get_risk_severity)
export const RISK_BANDS = {
  info: { min: 0, max: 10 },
  low: { min: 10, max: 30 },
  medium: { min: 30, max: 70 },
  high: { min: 70, max: 90 },
  critical: { min: 90, max: 100 }
};

export const getRiskSeverity = (risk) => {
  if (risk >= RISK_BANDS.critical.min) return 'critical';
  if (risk >= RISK_BANDS.high.min) return 'high';
  if (risk >= RISK_BANDS.medium.min) return 'medium';
  if (risk >= RISK_BANDS.low.min) return 'low';
  return 'info';
};

const riskRangeJson = (min, max) =>
  `{"and":[{"name":"risk","op":">=","val":"${min}"},{"name":"risk","op":"<","val":"${max}"}]}`;

const buildRiskTypeFilter = (min, max) => {
  const base = riskRangeJson(min, max);
  return {
    all: base,
    confirmed: `{"and":[${base},{"name":"confirmed","op":"==","val":"true"}]}`,
    notClosed: `{"and":[{"name":"status","op":"!=","val":"closed"},${base}]}`,
    notClosedAndConfirmed: `{"and":[{"and":[{"name":"confirmed","op":"==","val":"true"},{"name":"status","op":"!=","val":"closed"}]},${base}]}`
  };
};

const commandIdFilter = (id) => ({
  name: 'creator_command_id',
  op: eq.name.value,
  val: id
});

const vulnerabilitiesBySeverity = (severityType) => ({
  name: 'severity',
  op: eq.name.value,
  val: severityType
});

const vulnerabilitiesByStatus = (statusType) => ({
  name: 'status',
  op: eq.name.value,
  val: statusType
});

const vulnerabilityById = (id) => ({
  name: 'id',
  op: eq.name.value,
  val: id
});

const vulnerabilitiesByType = (type) => ({
  name: 'type',
  op: eq.name.value,
  val: type
});

const assetsByIp = (ip) => ({
  name: 'ip',
  op: eq.name.value,
  val: ip
});

const assetsByServiceName = (serviceName) => ({
  name: 'services',
  op: 'any',
  val: {
    name: 'name',
    op: eq.name.value,
    val: serviceName
  }
});

const vulnsByTag = (tag) => ({
  name: 'tags__name',
  op: eq.name.value,
  val: tag
});

export const RISK_TAGS = {
  cisaKev: 'CISA',
  exploitAvailable: 'exploit-available',
  trending: 'trending'
};

const assetsById = (id) => ({
  name: 'id',
  op: eq.name.value,
  val: id
});

// ------------------------------------

const SEARCH_BY_AUTOCOMPLETE_KEYS = {
  critical: createNameKey('Critical', 'critical'),
  high: createNameKey('High', 'high'),
  medium: createNameKey('Medium', 'medium'),
  low: createNameKey('Low', 'low'),
  informational: createNameKey('Informational', 'informational'),
  unclassified: createNameKey('Unclassified', 'unclassified'),
  true: createNameKey('True', 'true'),
  false: createNameKey('False', 'false'),
  trivial: createNameKey('Trivial', 'trivial'),
  simple: createNameKey('Simple', 'simple'),
  moderate: createNameKey('Moderate', 'moderate'),
  difficult: createNameKey('Difficult', 'difficult'),
  infeasible: createNameKey('Infeasible', 'infeasible'),
  vulnerability: createNameKey('Vulnerability', 'vulnerability'),
  vulnerability_web: createNameKey('Vulnerability Web', 'vulnerability_web'),
  open: createNameKey('Open', 'open'),
  closed: createNameKey('Closed', 'closed'),
  filtered: createNameKey('Filtered', 'filtered'),
  reOpened: createNameKey('Re-Opened', 're-opened'),
  riskAccepted: createNameKey('Risk Accepted', 'risk-accepted'),
  locked: createNameKey('Locked', 'true'),
  unlocked: createNameKey('Unlocked', 'false'),
  active: createNameKey('Active', 'true'),
  archived: createNameKey('Archived', 'false'),
  monthlyEvolutionBySeverity: createNameKey('Monthly evolution of vulnerabilities by Severity', 'monthly_evolution_by_severity'),
  monthlyEvolutionByStatus: createNameKey('Monthly evolution of vulnerabilities by Status', 'monthly_evolution_by_status'),
  topTenMostAffected: createNameKey('Top 10 most affected assets', 'top_ten_most_affected_hosts'),
  topTenMostRepeated: createNameKey('Most repeated vulnerabilities', 'top_ten_most_repeated_vulns'),
  vulnsPerHosts: createNameKey('Vulnerabilities per Assets', 'vulnerabilities_per_host'),
  vulnsPerSeverity: createNameKey('Vulnerabilities per severity', 'vulnerabilities_per_severity'),
  vulnsPerStatus: createNameKey('Vulnerabilities per status', 'vulnerabilities_per_status'),
  vulnsByRiskScore: createNameKey('Vulnerabilities by Risk Score', 'vulnerabilities_by_risk_score'),
  agents: createNameKey('Runners', 'agents'),
  assets: createNameKey('assets', 'hosts'),
  comments: createNameKey('comments', 'comments'),
  pipelines: createNameKey('pipelines', 'pipelines'),
  planner: createNameKey('planner', 'planner'),
  reports: createNameKey('reports', 'reports'),
  users: createNameKey('users', 'users'),
  vulnerabilities: createNameKey('vulnerabilities', 'vulnerabilities'),
  workspaces: createNameKey('workspaces', 'workspaces'),
  advanced: createNameKey('advanced', 'advanced'),
  impactAccountability: createNameKey('Accountability', 'accountability'),
  impactAvailability: createNameKey('Availability', 'availability'),
  impactConfidentiality: createNameKey('Confidentiality', 'confidentiality'),
  impactIntegrity: createNameKey('Integrity', 'integrity'),
  issueTrackerJira: createNameKey('Jira', 'jira'),
  issueTrackerServiceNow: createNameKey('ServiceNow', 'servicenow'),
  issueTrackerWebHelpDesk: createNameKey('Web Help Desk', 'whd'),
  issueTrackerGitLab: createNameKey('GitLab', 'gitlab'),
  issueTrackerServiceDesk: createNameKey('ServiceDesk', 'mesd'),
  online: createNameKey('Online', 'online'),
  offline: createNameKey('Offline', 'offline')
};

const SEVERITY_AUTOCOMPLETE = [
  SEARCH_BY_AUTOCOMPLETE_KEYS.critical,
  SEARCH_BY_AUTOCOMPLETE_KEYS.high,
  SEARCH_BY_AUTOCOMPLETE_KEYS.medium,
  SEARCH_BY_AUTOCOMPLETE_KEYS.low,
  SEARCH_BY_AUTOCOMPLETE_KEYS.informational,
  SEARCH_BY_AUTOCOMPLETE_KEYS.unclassified
];

const BOOL_AUTOCOMPLETE = [
  SEARCH_BY_AUTOCOMPLETE_KEYS.true,
  SEARCH_BY_AUTOCOMPLETE_KEYS.false
];

const EASE_OF_RESOLUTION_AUTOCOMPLETE = [
  SEARCH_BY_AUTOCOMPLETE_KEYS.trivial,
  SEARCH_BY_AUTOCOMPLETE_KEYS.simple,
  SEARCH_BY_AUTOCOMPLETE_KEYS.moderate,
  SEARCH_BY_AUTOCOMPLETE_KEYS.difficult,
  SEARCH_BY_AUTOCOMPLETE_KEYS.infeasible
];

const VULN_TYPE_AUTOCOMPLETE = [
  SEARCH_BY_AUTOCOMPLETE_KEYS.vulnerability,
  SEARCH_BY_AUTOCOMPLETE_KEYS.vulnerability_web
];

const STATUS_AUTOCOMPLETE = [
  SEARCH_BY_AUTOCOMPLETE_KEYS.open,
  SEARCH_BY_AUTOCOMPLETE_KEYS.closed,
  SEARCH_BY_AUTOCOMPLETE_KEYS.reOpened,
  SEARCH_BY_AUTOCOMPLETE_KEYS.riskAccepted
];

const SERVICES_STATUS_AUTOCOMPLETE = [
  SEARCH_BY_AUTOCOMPLETE_KEYS.open,
  SEARCH_BY_AUTOCOMPLETE_KEYS.closed,
  SEARCH_BY_AUTOCOMPLETE_KEYS.filtered
];

const WORKSPACES_PERMISSION_AUTOCOMPLETE = [
  SEARCH_BY_AUTOCOMPLETE_KEYS.locked,
  SEARCH_BY_AUTOCOMPLETE_KEYS.unlocked
];

const AGENT_STATUS_AUTOCOMPLETE = [
  SEARCH_BY_AUTOCOMPLETE_KEYS.online,
  SEARCH_BY_AUTOCOMPLETE_KEYS.offline
];

const GRAPH_TYPE_AUTOCOMPLETE = [
  SEARCH_BY_AUTOCOMPLETE_KEYS.monthlyEvolutionBySeverity,
  SEARCH_BY_AUTOCOMPLETE_KEYS.monthlyEvolutionByStatus,
  SEARCH_BY_AUTOCOMPLETE_KEYS.topTenMostAffected,
  SEARCH_BY_AUTOCOMPLETE_KEYS.topTenMostRepeated,
  SEARCH_BY_AUTOCOMPLETE_KEYS.vulnsPerHosts,
  SEARCH_BY_AUTOCOMPLETE_KEYS.vulnsPerSeverity,
  SEARCH_BY_AUTOCOMPLETE_KEYS.vulnsPerStatus,
  SEARCH_BY_AUTOCOMPLETE_KEYS.vulnsByRiskScore
];

const NOTIFICATION_TYPE_AUTOCOMPLETE = [
  SEARCH_BY_AUTOCOMPLETE_KEYS.agents,
  SEARCH_BY_AUTOCOMPLETE_KEYS.assets,
  SEARCH_BY_AUTOCOMPLETE_KEYS.comments,
  SEARCH_BY_AUTOCOMPLETE_KEYS.pipelines,
  SEARCH_BY_AUTOCOMPLETE_KEYS.planner,
  SEARCH_BY_AUTOCOMPLETE_KEYS.reports,
  SEARCH_BY_AUTOCOMPLETE_KEYS.users,
  SEARCH_BY_AUTOCOMPLETE_KEYS.vulnerabilities,
  SEARCH_BY_AUTOCOMPLETE_KEYS.workspaces,
  SEARCH_BY_AUTOCOMPLETE_KEYS.advanced
];

const ISSUE_TRACKER_AUTOCOMPLETE = [
  SEARCH_BY_AUTOCOMPLETE_KEYS.issueTrackerJira,
  SEARCH_BY_AUTOCOMPLETE_KEYS.issueTrackerServiceNow,
  SEARCH_BY_AUTOCOMPLETE_KEYS.issueTrackerWebHelpDesk,
  SEARCH_BY_AUTOCOMPLETE_KEYS.issueTrackerGitLab,
  SEARCH_BY_AUTOCOMPLETE_KEYS.issueTrackerServiceDesk
];

const IMPACT_AUTOCOMPLETE = [
  SEARCH_BY_AUTOCOMPLETE_KEYS.impactAccountability,
  SEARCH_BY_AUTOCOMPLETE_KEYS.impactAvailability,
  SEARCH_BY_AUTOCOMPLETE_KEYS.impactConfidentiality,
  SEARCH_BY_AUTOCOMPLETE_KEYS.impactIntegrity
];

export const CUSTOM_FIELDS_OPERATORS = {
  str: [eq, neq, ilike],
  int: [eq, neq, greaterOrEqual, greater, lessOrEqual, less],
  float: [eq, neq, greaterOrEqual, greater, lessOrEqual, less],
  list: [any, notAny, ilike],
  choice: [eq, neq],
  date: [eq, lessOrEqual, greaterOrEqual, less, greater],
  markdown: [eq, neq, ilike]
};

export const SEARCH_BY_FILTER_KEYS = {
  active: searchByFilterKey({ label: 'Active', value: 'active' }, BOOL_AUTOCOMPLETE, [eq]),
  agentStatus: searchByFilterKey({ label: 'Status', value: 'status' }, AGENT_STATUS_AUTOCOMPLETE, [eq, neq]),
  asset: searchByFilterKey({ label: 'Asset', value: 'target' }, [], [neq, ilike, isOneOf]),
  blocked: searchByFilterKey({ label: 'Blocked', value: 'blocked' }, BOOL_AUTOCOMPLETE, [eq, neq]),
  cloudAgentName: searchByFilterKey({ label: 'Security Scanner name', value: 'cloud_agent' }, [], [eq, neq, like]),
  assetOs: searchByFilterKey({ label: 'Asset OS', value: 'target_host_os' }, [], [eq, neq, ilike]),
  categories: searchByFilterKey({ label: 'Categories', value: 'categories' }, [], [isOneOfList, isNotOneOfList]),
  category: searchByFilterKey({ label: 'Category', value: 'category' }, [], [isOneOfList, isNotOneOfList]),
  toolCategory: searchByFilterKey({ label: 'Tool Category', value: 'category' }, [], [isOneOfList, isNotOneOfList]),
  confirmed: searchByFilterKey({ label: 'Confirmed', value: 'confirmed' }, BOOL_AUTOCOMPLETE, [eq]),
  createDate: searchByFilterKey({ label: 'Creation Date', value: 'create_date' }, [], [eq, lessOrEqual, greaterOrEqual, less, greater, range]),
  creationTime: searchByFilterKey({ label: 'Creation Time', value: 'create_date' }, [], [eq, lessOrEqual, greaterOrEqual, less, greater, range]),
  command: searchByFilterKey({ label: 'Command', value: 'creator_command_id' }, [], [eq, greater, greaterOrEqual, less, lessOrEqual]),
  assetCommand: searchByFilterKey({ label: 'Creator Command Id', value: 'commands__id' }, [], [any, notAny]),
  assetCreatorTool: searchByFilterKey({ label: 'Creator Tool', value: 'creator_command_tool' }, [], [eq, neq, ilike]),
  creatorCommandId: searchByFilterKey({ label: 'Creator Command ID', value: 'creator_command_id' }, [], [eq, neq]),
  conclusions: searchByFilterKey({ label: 'Conclusions', value: 'conclusions' }, [], [eq, neq]),
  crontab: searchByFilterKey({ label: 'Crontab', value: 'crontab' }, [], [eq]),
  customer: searchByFilterKey({ label: 'Customer', value: 'customer' }, [], [eq, neq, ilike]),
  cve_instances__name: searchByFilterKey({ label: 'Name', value: 'name' }, [], [isOneOf, isNotOneOf, ilike]),
  cve_instances: searchByFilterKey({ label: 'CVE', value: 'cve_instances__name' }, [], [any, notAny]),
  cvss2: searchByFilterKey({ label: 'CVSS2', value: 'cvss2_base_score' }, [], [eq, neq, greaterOrEqual, greater, lessOrEqual, less]),
  cvss3: searchByFilterKey({ label: 'CVSS3', value: 'cvss3_base_score' }, [], [eq, neq, greaterOrEqual, greater, lessOrEqual, less]),
  cvss4: searchByFilterKey({ label: 'CVSS4', value: 'cvss4_base_score' }, [], [eq, neq, greaterOrEqual, greater, lessOrEqual, less]),
  cwe__name: searchByFilterKey({ label: 'Name', value: 'name' }, [], [isOneOf, isNotOneOf, ilike]),
  cwe: searchByFilterKey({ label: 'CWE', value: 'cwe__name' }, [], [any, notAny]),
  data: searchByFilterKey({ label: 'Data', value: 'data' }, [], [eq, neq, ilike]),
  description: searchByFilterKey({ label: 'Description', value: 'description' }, [], [eq, neq, ilike]),
  commands__id: searchByFilterKey({ label: 'ID', value: 'id' }, [], [eq, greater, greaterOrEqual, less, lessOrEqual]),
  commands__params: searchByFilterKey({ label: 'Parameters', value: 'params' }, [], [eq, neq, ilike]),
  creator__username: searchByFilterKey({ label: 'Username', value: 'username' }, [], [eq, neq, ilike]),
  easeOfResolution: searchByFilterKey({ label: 'Ease of Resolution', value: 'ease_of_resolution' }, EASE_OF_RESOLUTION_AUTOCOMPLETE, [eq, neq]),
  endDate: searchByFilterKey({ label: 'End Date', value: 'end_date' }, [], [eq, lessOrEqual, greaterOrEqual, less, greater, range]),
  externalId: searchByFilterKey({ label: 'External ID', value: 'external_id' }, [], [eq, neq, ilike]),
  executor__name: searchByFilterKey({ label: 'Name', value: 'name' }, [], [eq, neq, ilike]),
  executorName: searchByFilterKey({ label: 'Executor name', value: 'executor__name' }, [], [has]),
  graphType: searchByFilterKey({ label: 'Type', value: 'type' }, GRAPH_TYPE_AUTOCOMPLETE, [eq, neq]),
  grouped: searchByFilterKey({ label: 'Grouped', value: 'grouped' }, BOOL_AUTOCOMPLETE, [eq]),
  host__ip: searchByFilterKey({ label: 'Ip', value: 'ip' }, [], [eq, neq, ilike]),
  host: searchByFilterKey({ label: 'Asset', value: 'host__ip' }, [], [has]),
  hostnames__name: searchByFilterKey({ label: 'Name', value: 'name' }, [], [isNotOneOf, ilike, isOneOf]),
  hostNames: searchByFilterKey({ label: 'Hostnames', value: 'hostnames__name' }, [], [any, notAny]),
  id: searchByFilterKey({ label: 'ID', value: 'id' }, [], [eq, neq, greaterOrEqual, greater, lessOrEqual, less]),
  impact: searchByFilterKey({ label: 'Impact', value: 'impact' }, BOOL_AUTOCOMPLETE, IMPACT_AUTOCOMPLETE),
  importance: searchByFilterKey({ label: 'Importance', value: 'importance' }, [], [eq, greater, greaterOrEqual, less, lessOrEqual]),
  ip: searchByFilterKey({ label: 'Asset', value: 'ip' }, [], [neq, ilike, isOneOf]),
  issueTracker: searchByFilterKey({ label: 'Issue Tracker', value: 'issueTracker' }, ISSUE_TRACKER_AUTOCOMPLETE, [eq]),
  lastDetected: searchByFilterKey({ label: 'Last Detected', value: 'last_detected' }, [], [eq, lessOrEqual, greaterOrEqual, less, greater]),
  lastModified: searchByFilterKey({ label: 'Last Modified', value: 'update_date' }, [], [eq, greaterOrEqual, lessOrEqual, greater, less, range]),
  lastRun: searchByFilterKey({ label: 'Last run', value: 'last_run' }, [], [eq, greaterOrEqual, lessOrEqual, less, greater]),
  lastExecutionDate: searchByFilterKey({ label: 'Last Execution Date', value: 'last_execution_date' }, [], [eq, greaterOrEqual, lessOrEqual, less, greater]),
  lastExecutionTool: searchByFilterKey({ label: 'Last Execution Tool', value: 'last_execution_tool' }, [], CUSTOM_FIELDS_OPERATORS.str),
  important: searchByFilterKey({ label: 'Importance', value: 'importance' }, BOOL_AUTOCOMPLETE, [eq]),
  assetName: searchByFilterKey({ label: 'Name', value: 'ip' }, [], [eq, neq, ilike, isOneOf]),
  mac: searchByFilterKey({ label: 'Mac Address', value: 'mac' }, [], [eq, neq, ilike]),
  message: searchByFilterKey({ label: 'Message', value: 'message' }, [], [eq, neq, ilike]),
  method: searchByFilterKey({ label: 'Method', value: 'method' }, [], [eq, neq, ilike]),
  name: searchByFilterKey({ label: 'Name', value: 'name' }, [], [eq, neq, ilike]),
  agentName: searchByFilterKey({ label: 'Agent Name', value: 'agent_name' }, [], [eq, neq, ilike]),
  notificationType: searchByFilterKey({ label: 'Type', value: 'type' }, NOTIFICATION_TYPE_AUTOCOMPLETE, [eq, neq]),
  objectives: searchByFilterKey({ label: 'Objectives', value: 'objectives' }, [], [eq, neq, ilike]),
  openServices: searchByFilterKey({ label: 'Open Services', value: 'services__name' }, [], [any, notAny]),
  os: searchByFilterKey({ label: 'OS', value: 'os' }, [], [eq, neq, ilike]),
  owned: searchByFilterKey({ label: 'Owned', value: 'owned' }, BOOL_AUTOCOMPLETE, [eq]),
  owner: searchByFilterKey({ label: 'Owner', value: 'owner' }, [], [eq, neq, ilike]),
  assetOwner: searchByFilterKey({ label: 'Owner', value: 'creator__username' }, [], [has]),
  parameters: searchByFilterKey({ label: 'Parameters', value: 'commands__params' }, [], [any, notAny]),
  path: searchByFilterKey({ label: 'Path', value: 'path' }, [], [eq, neq, ilike]),
  permission: searchByFilterKey({ label: 'Permission', value: 'readonly' }, WORKSPACES_PERMISSION_AUTOCOMPLETE, [eq, neq]),
  port: searchByFilterKey({ label: 'Port', value: 'port' }, [], [eq, neq, greaterOrEqual, greater, lessOrEqual, less]),
  protocol: searchByFilterKey({ label: 'Protocol', value: 'protocol' }, [], [eq, neq, ilike]),
  public: searchByFilterKey({ label: 'Public', value: 'public' }, BOOL_AUTOCOMPLETE, [eq]),
  query: searchByFilterKey({ label: 'Query', value: 'query_string' }, [], [eq, neq, ilike]),
  recommendations: searchByFilterKey({ label: 'Recommendations', value: 'recommendations' }, [], [eq, neq, ilike]),
  request: searchByFilterKey({ label: 'Request', value: 'request' }, [], [eq, neq, ilike]),
  resolution: searchByFilterKey({ label: 'Resolution', value: 'resolution' }, [], [eq, neq, ilike]),
  response: searchByFilterKey({ label: 'Response', value: 'response' }, [], [eq, neq, ilike]),
  risk_score: searchByFilterKey({ label: 'Risk Score', value: 'risk' }, [], [eq, neq, greaterOrEqual, greater, lessOrEqual, less]),
  totalRiskScore: searchByFilterKey({ label: 'Total Risk Score', value: 'risk' }, [], [eq, neq, greaterOrEqual, greater, lessOrEqual, less]),
  scope: searchByFilterKey({ label: 'Scope', value: 'scope' }, [], [eq, neq, ilike]),
  scope__name: searchByFilterKey({ label: 'Name', value: 'name' }, [], [ilike]),
  scopeInWs: searchByFilterKey({ label: 'Scope', value: 'scope__name' }, [], [any]),
  search: searchByFilterKey({ label: 'Search', value: 'ip' }, [], [neq, ilike, isOneOf]),
  service__name: searchByFilterKey({ label: 'Name', value: 'name' }, [], [eq, neq, ilike]),
  service__port: searchByFilterKey({ label: 'Port', value: 'port' }, [], [eq, neq, greaterOrEqual, greater, lessOrEqual, less]),
  serviceName: searchByFilterKey({ label: 'Service name', value: 'service__name' }, [], [has]),
  servicePort: searchByFilterKey({ label: 'Service port', value: 'service__port' }, [], [has]),
  services__name: searchByFilterKey({ label: 'Name', value: 'name' }, [], [eq, neq, ilike]),
  services__port: searchByFilterKey({ label: 'Port', value: 'port' }, [], [eq, neq, greaterOrEqual, greater, lessOrEqual, less]),
  services__status: searchByFilterKey({ label: 'Status', value: 'status' }, SERVICES_STATUS_AUTOCOMPLETE, [eq, neq]),
  services__version: searchByFilterKey({ label: 'Version', value: 'version' }, [], [eq, neq, ilike]),
  servicesName: searchByFilterKey({ label: 'Services', value: 'services__name' }, [], [any, notAny]),
  servicesPort: searchByFilterKey({ label: 'Services port', value: 'services__port' }, [], [any]),
  servicesStatus: searchByFilterKey({ label: 'Services status', value: 'services__status' }, [], [any]),
  servicesVersion: searchByFilterKey({ label: 'Services version', value: 'services__version' }, [], [any]),
  severity: searchByFilterKey({ label: 'Severity', value: 'severity' }, SEVERITY_AUTOCOMPLETE, [eq, neq]),
  startDate: searchByFilterKey({ label: 'Start Date', value: 'start_date' }, [], [eq, lessOrEqual, greaterOrEqual, less, greater, range]),
  status: searchByFilterKey({ label: 'Status', value: 'status' }, STATUS_AUTOCOMPLETE, [eq, neq]),
  statusCode: searchByFilterKey({ label: 'Status Code', value: 'status_code' }, [], [eq, neq, greaterOrEqual, greater, lessOrEqual, less]),
  status_history__status: searchByFilterKey({ label: 'Status', value: 'status' }, STATUS_AUTOCOMPLETE, [isOneOf, notOneOf]),
  statusHistory: searchByFilterKey({ label: 'Status Trace', value: 'status_history__status' }, [], [any, notAny]),
  statusHistoryDate: searchByFilterKey({ label: 'Status Trace Date', value: 'status_history__change_date' }, [], [range]),
  statusInServices: searchByFilterKey({ label: 'Status', value: 'status' }, SERVICES_STATUS_AUTOCOMPLETE, [eq, neq]),
  summary: searchByFilterKey({ label: 'Summary', value: 'summary' }, [], [eq, neq, ilike]),
  tag_instances__name: searchByFilterKey({ label: 'Name', value: 'name' }, [], [isNotOneOf, ilike, isOneOf]),
  tag_instances: searchByFilterKey({ label: 'Tag', value: 'tag_instances__name' }, [], [any, notAny]),
  tags__name: searchByFilterKey({ label: 'Tags', value: 'tags__name' }, [], [eq, neq, ilike]),
  template_name: searchByFilterKey({ label: 'Template name', value: 'template_name' }, [], [eq, neq, ilike]),
  time: searchByFilterKey({ label: 'Time', value: 'event_date' }, [], [eq, lessOrEqual, greaterOrEqual, less, greater]),
  title: searchByFilterKey({ label: 'Title name', value: 'title' }, [], [eq, neq]),
  tool: searchByFilterKey({ label: 'Creator Tool', value: 'tool' }, [], [eq, neq, ilike]),
  tools: searchByFilterKey({ label: 'Tools', value: 'tools' }, [], [isOneOfList, isNotOneOfList]),
  toolsCategories: searchByFilterKey({ label: 'Tools Categories', value: 'tools_categories' }, [], [isOneOf, notOneOf]),
  triggeredByAgent: searchByFilterKey({ label: 'Triggered By', value: 'triggered_by' }, [], [eq, neq, ilike]),
  user__username: searchByFilterKey({ label: 'Username', value: 'username' }, [], [eq, neq, ilike]),
  triggered_by__username: searchByFilterKey({ label: 'Triggered By', value: 'triggered_by__username' }, [], [has]),
  type: searchByFilterKey({ label: 'Type', value: 'type' }, VULN_TYPE_AUTOCOMPLETE, [eq, neq]),
  updateDate: searchByFilterKey({ label: 'Update Date', value: 'update_date' }, [], [eq, greaterOrEqual, lessOrEqual, greater, less, range]),
  version: searchByFilterKey({ label: 'Version', value: 'version' }, [], [eq, neq, ilike]),
  vuln_count: searchByFilterKey({ label: 'Vuln Count', value: 'vuln_count' }, [], [eq, less, greater]),
  vulnerabilities: searchByFilterKey({ label: 'Vulnerabilities', value: 'vulnerability_count' }, [], [eq, greater, greaterOrEqual, less, lessOrEqual]),
  website: searchByFilterKey({ label: 'Website', value: 'website' }, [], [eq, neq, ilike]),
  workspace__name: searchByFilterKey({ label: 'Name', value: 'name' }, [], [eq, neq, ilike]),
  workspaceName: searchByFilterKey({ label: 'Workspace', value: 'workspace__name' }, [], [has]),
  clientEndpoint: searchByFilterKey({ label: 'Client Endpoint', value: 'endpoint' }, [], [eq, neq, ilike]),
  username: searchByFilterKey({ label: 'Username', value: 'username' }, [], [eq, neq, ilike]),
  leakDate: searchByFilterKey({ label: 'Leak Date', value: 'leak_date' }, [], [eq, lessOrEqual, greaterOrEqual, less, greater]),
  password: searchByFilterKey({ label: 'Password', value: 'password' }, [], [eq, neq, ilike]),
  vulnerabilities__name: searchByFilterKey({ label: 'Name', value: 'name' }, [], [eq, neq, ilike]),
  linkedVulnerability: searchByFilterKey({ label: 'Linked Vulnerability', value: 'vulnerabilities__name' }, [], [any]),
  workspaces: searchByFilterKey({ label: 'Workspaces', value: 'workspaces' }, [], [isOneOf, notOneOf]),
  workspacesStatusLog: searchByFilterKey({ label: 'Workspaces', value: 'workspaces' }, [], [isOneOfList, isNotOneOfList])
};

export const GROUP_BY = {
  none: {
    key: groupByKey('None', ''),
    filter: null
  },
  name: {
    key: groupByKey('Name', 'name'),
    filter: createFilter('name', ilike.name.value, '')
  },
  severity: {
    key: groupByKey('Severity', 'severity'),
    filter: createFilter('severity', eq.name.value, '')
  },
  services__name: {
    key: groupByKey('Service name', 'services__name'),
    filter: createFilter('services', any.name.value, '')
  },
  status: {
    key: groupByKey('Status', 'status'),
    filter: createFilter('status', eq.name.value, '')
  },
  hostnames__name: {
    key: groupByKey('Hostnames', 'hostnames__name'),
    filter: createFilter('hostnames', any.name.value, '')
  },
  target_host_ip: {
    key: groupByKey('Asset', 'target_host_ip'),
    filter: createFilter('target_host_ip', ilike.name.value, '')
  },
  os: {
    key: groupByKey('OS', 'os'),
    filter: createFilter('os', eq.name.value, '')
  },
  cveAndTarget: {
    key: {
      label: 'CVE & Asset',
      value: [
        {
          field: 'cve_instances__name'
        },
        {
          field: 'target'
        }
      ]
    },
    filter: createFilter('cve_instances__name', eq.name.value, '')
  }
};

export const CONTEXT_MENU_FILTER_KEYS = {
  asset: {
    key: createLabelName('Asset', 'target'),
    filter: createFilter('target', eq.name.value, '')
  },
  date: {
    key: createLabelName('Date', 'create_date'),
    filter: createFilter('create_date', eq.name.value, '')
  },
  name: {
    key: createLabelName('Name', 'name'),
    filter: createFilter('name', ilike.name.value, '')
  },
  service: {
    key: createLabelName('Service name', 'service'),
    filter: createFilter('service', has.name.value, '')
  },
  status: {
    key: createLabelName('Status', 'status'),
    filter: createFilter('status', eq.name.value, '')
  },
  tags: {
    key: createLabelName('Tags', 'tags'),
    filter: createFilter('tags', has.value, '')
  }
};

export const VULNS_BY_RISK_TYPE_FILTERS = {
  informational_risk_count: buildRiskTypeFilter(RISK_BANDS.info.min, RISK_BANDS.info.max),
  low_risk_count: buildRiskTypeFilter(RISK_BANDS.low.min, RISK_BANDS.low.max),
  medium_risk_count: buildRiskTypeFilter(RISK_BANDS.medium.min, RISK_BANDS.medium.max),
  high_risk_count: buildRiskTypeFilter(RISK_BANDS.high.min, RISK_BANDS.high.max),
  critical_risk_count: buildRiskTypeFilter(RISK_BANDS.critical.min, RISK_BANDS.critical.max)
};
// --- VULN KEYS ---

export const VULNS_SEARCH_BY_FILTER_KEYS = [
  SEARCH_BY_FILTER_KEYS.asset,
  SEARCH_BY_FILTER_KEYS.assetOs,
  SEARCH_BY_FILTER_KEYS.confirmed,
  SEARCH_BY_FILTER_KEYS.createDate,
  SEARCH_BY_FILTER_KEYS.creatorCommandId,
  SEARCH_BY_FILTER_KEYS.cve_instances,
  SEARCH_BY_FILTER_KEYS.cvss2,
  SEARCH_BY_FILTER_KEYS.cvss3,
  SEARCH_BY_FILTER_KEYS.cvss4,
  SEARCH_BY_FILTER_KEYS.cwe,
  SEARCH_BY_FILTER_KEYS.data,
  SEARCH_BY_FILTER_KEYS.description,
  SEARCH_BY_FILTER_KEYS.easeOfResolution,
  SEARCH_BY_FILTER_KEYS.externalId,
  SEARCH_BY_FILTER_KEYS.id,
  SEARCH_BY_FILTER_KEYS.impact,
  SEARCH_BY_FILTER_KEYS.issueTracker,
  SEARCH_BY_FILTER_KEYS.lastDetected,
  SEARCH_BY_FILTER_KEYS.method,
  SEARCH_BY_FILTER_KEYS.name,
  SEARCH_BY_FILTER_KEYS.path,
  SEARCH_BY_FILTER_KEYS.query,
  SEARCH_BY_FILTER_KEYS.request,
  SEARCH_BY_FILTER_KEYS.resolution,
  SEARCH_BY_FILTER_KEYS.response,
  SEARCH_BY_FILTER_KEYS.risk_score,
  SEARCH_BY_FILTER_KEYS.serviceName,
  SEARCH_BY_FILTER_KEYS.servicePort,
  SEARCH_BY_FILTER_KEYS.severity,
  SEARCH_BY_FILTER_KEYS.status,
  SEARCH_BY_FILTER_KEYS.statusCode,
  SEARCH_BY_FILTER_KEYS.statusHistory,
  SEARCH_BY_FILTER_KEYS.statusHistoryDate,
  SEARCH_BY_FILTER_KEYS.tag_instances,
  SEARCH_BY_FILTER_KEYS.tool,
  SEARCH_BY_FILTER_KEYS.type,
  SEARCH_BY_FILTER_KEYS.updateDate,
  SEARCH_BY_FILTER_KEYS.website
];
// --- VULN NEW REPORTS KEYS ---

export const VULNS_NEW_REPORTS_SEARCH_BY_FILTER_KEYS = [
  SEARCH_BY_FILTER_KEYS.assetOs,
  SEARCH_BY_FILTER_KEYS.confirmed,
  SEARCH_BY_FILTER_KEYS.createDate,
  SEARCH_BY_FILTER_KEYS.creatorCommandId,
  SEARCH_BY_FILTER_KEYS.cve_instances,
  SEARCH_BY_FILTER_KEYS.cvss2,
  SEARCH_BY_FILTER_KEYS.cvss3,
  SEARCH_BY_FILTER_KEYS.cvss4,
  SEARCH_BY_FILTER_KEYS.cwe,
  SEARCH_BY_FILTER_KEYS.data,
  SEARCH_BY_FILTER_KEYS.description,
  SEARCH_BY_FILTER_KEYS.easeOfResolution,
  SEARCH_BY_FILTER_KEYS.externalId,
  SEARCH_BY_FILTER_KEYS.id,
  SEARCH_BY_FILTER_KEYS.impact,
  SEARCH_BY_FILTER_KEYS.issueTracker,
  SEARCH_BY_FILTER_KEYS.lastDetected,
  SEARCH_BY_FILTER_KEYS.method,
  SEARCH_BY_FILTER_KEYS.name,
  SEARCH_BY_FILTER_KEYS.path,
  SEARCH_BY_FILTER_KEYS.query,
  SEARCH_BY_FILTER_KEYS.request,
  SEARCH_BY_FILTER_KEYS.resolution,
  SEARCH_BY_FILTER_KEYS.response,
  SEARCH_BY_FILTER_KEYS.risk_score,
  SEARCH_BY_FILTER_KEYS.serviceName,
  SEARCH_BY_FILTER_KEYS.servicePort,
  SEARCH_BY_FILTER_KEYS.severity,
  SEARCH_BY_FILTER_KEYS.status,
  SEARCH_BY_FILTER_KEYS.statusCode,
  SEARCH_BY_FILTER_KEYS.statusHistory,
  SEARCH_BY_FILTER_KEYS.statusHistoryDate,
  SEARCH_BY_FILTER_KEYS.tag_instances,
  SEARCH_BY_FILTER_KEYS.tool,
  SEARCH_BY_FILTER_KEYS.type,
  SEARCH_BY_FILTER_KEYS.updateDate,
  SEARCH_BY_FILTER_KEYS.website
];

export const NEW_REPORTS_SEARCH_BY_FILTER_KEYS = [
  SEARCH_BY_FILTER_KEYS.name
];

export const VULNS_GROUP_BY_KEYS = [
  GROUP_BY.none.key,
  GROUP_BY.name.key,
  GROUP_BY.severity.key,
  GROUP_BY.status.key,
  GROUP_BY.target_host_ip.key,
  GROUP_BY.cveAndTarget.key
];

export const VULNS_CONTEXT_MENU_FILTER_KEYS = [
  CONTEXT_MENU_FILTER_KEYS.name.key,
  CONTEXT_MENU_FILTER_KEYS.service.key,
  CONTEXT_MENU_FILTER_KEYS.asset.key,
  CONTEXT_MENU_FILTER_KEYS.date.key,
  CONTEXT_MENU_FILTER_KEYS.status.key,
  CONTEXT_MENU_FILTER_KEYS.tags.key
];

// --- ASSETS KEYS ---

export const ASSETS_SEARCH_BY_FILTER_KEYS = [
  SEARCH_BY_FILTER_KEYS.creationTime,
  SEARCH_BY_FILTER_KEYS.assetCommand,
  SEARCH_BY_FILTER_KEYS.assetCreatorTool,
  SEARCH_BY_FILTER_KEYS.description,
  SEARCH_BY_FILTER_KEYS.hostNames,
  SEARCH_BY_FILTER_KEYS.id,
  SEARCH_BY_FILTER_KEYS.importance,
  SEARCH_BY_FILTER_KEYS.mac,
  SEARCH_BY_FILTER_KEYS.assetName,
  SEARCH_BY_FILTER_KEYS.openServices,
  SEARCH_BY_FILTER_KEYS.os,
  SEARCH_BY_FILTER_KEYS.owned,
  SEARCH_BY_FILTER_KEYS.assetOwner,
  SEARCH_BY_FILTER_KEYS.parameters,
  SEARCH_BY_FILTER_KEYS.totalRiskScore,
  SEARCH_BY_FILTER_KEYS.servicesName,
  SEARCH_BY_FILTER_KEYS.servicesPort,
  SEARCH_BY_FILTER_KEYS.servicesStatus,
  SEARCH_BY_FILTER_KEYS.servicesVersion,
  SEARCH_BY_FILTER_KEYS.tags__name,
  SEARCH_BY_FILTER_KEYS.lastModified,
  SEARCH_BY_FILTER_KEYS.vulnerabilities,
  SEARCH_BY_FILTER_KEYS.workspaceName
];

export const ASSETS_GROUP_BY_KEYS = [
  GROUP_BY.none.key,
  GROUP_BY.os.key,
  GROUP_BY.services__name.key,
  GROUP_BY.hostnames__name.key
];

export const REPORT_FILTERS = [
  {
    name: createLabelValue('Confirmed', 'confirmed'),
    filter: createFilter('confirmed', eq.name.value, 'true')
  },
  {
    name: createLabelValue('Not Confirmed', 'notConfirmed'),
    filter: createFilter('confirmed', eq.name.value, 'false')
  },
  {
    name: createLabelValue('Web', 'webVulnerability'),
    filter: createFilter('type', eq.name.value, 'vulnerability_web')
  },
  {
    name: createLabelValue('Vulnerability', 'vulnerability'),
    filter: createFilter('type', eq.name.value, 'vulnerability')
  },
  {
    name: createLabelValue('Critical', 'critical'),
    filter: createFilter('severity', eq.name.value, 'critical')
  },
  {
    name: createLabelValue('High', 'high'),
    filter: createFilter('severity', eq.name.value, 'high')
  },
  {
    name: createLabelValue('Medium', 'medium'),
    filter: createFilter('severity', eq.name.value, 'medium')
  },
  {
    name: createLabelValue('Low', 'low'),
    filter: createFilter('severity', eq.name.value, 'low')
  },
  {
    name: createLabelValue('Informational', 'informational'),
    filter: createFilter('severity', eq.name.value, 'informational')
  },
  {
    name: createLabelValue('Unclassified', 'unclassified'),
    filter: createFilter('severity', eq.name.value, 'unclassified')
  },
  {
    name: createLabelValue('Open', 'open'),
    filter: createFilter('status', eq.name.value, 'open')
  },
  {
    name: createLabelValue('Closed', 'closed'),
    filter: createFilter('status', eq.name.value, 'closed')
  },
  {
    name: createLabelValue('Re-Opened', 'reopened'),
    filter: createFilter('status', eq.name.value, 're-opened')
  },
  {
    name: createLabelValue('Risk-Accepted', 'riskAccepted'),
    filter: createFilter('status', eq.name.value, 'risk-accepted')
  }
];

// --- KB KEYS ---

export const KB_SEARCH_BY_FILTER_KEYS = [
  SEARCH_BY_FILTER_KEYS.data,
  SEARCH_BY_FILTER_KEYS.description,
  SEARCH_BY_FILTER_KEYS.easeOfResolution,
  SEARCH_BY_FILTER_KEYS.externalId,
  SEARCH_BY_FILTER_KEYS.id,
  SEARCH_BY_FILTER_KEYS.name,
  SEARCH_BY_FILTER_KEYS.resolution,
  SEARCH_BY_FILTER_KEYS.severity
];

// --- VULNS IN ASSET KEYS ---

export const VULNS_ASSETS_SEARCH_BY_FILTER_KEYS = [
  SEARCH_BY_FILTER_KEYS.confirmed,
  SEARCH_BY_FILTER_KEYS.createDate,
  SEARCH_BY_FILTER_KEYS.creatorCommandId,
  SEARCH_BY_FILTER_KEYS.cve_instances,
  SEARCH_BY_FILTER_KEYS.cvss2,
  SEARCH_BY_FILTER_KEYS.cvss3,
  SEARCH_BY_FILTER_KEYS.cvss4,
  SEARCH_BY_FILTER_KEYS.cwe,
  SEARCH_BY_FILTER_KEYS.data,
  SEARCH_BY_FILTER_KEYS.description,
  SEARCH_BY_FILTER_KEYS.easeOfResolution,
  SEARCH_BY_FILTER_KEYS.externalId,
  SEARCH_BY_FILTER_KEYS.id,
  SEARCH_BY_FILTER_KEYS.impact,
  SEARCH_BY_FILTER_KEYS.issueTracker,
  SEARCH_BY_FILTER_KEYS.lastDetected,
  SEARCH_BY_FILTER_KEYS.method,
  SEARCH_BY_FILTER_KEYS.name,
  SEARCH_BY_FILTER_KEYS.path,
  SEARCH_BY_FILTER_KEYS.query,
  SEARCH_BY_FILTER_KEYS.request,
  SEARCH_BY_FILTER_KEYS.resolution,
  SEARCH_BY_FILTER_KEYS.response,
  SEARCH_BY_FILTER_KEYS.serviceName,
  SEARCH_BY_FILTER_KEYS.servicePort,
  SEARCH_BY_FILTER_KEYS.severity,
  SEARCH_BY_FILTER_KEYS.status,
  SEARCH_BY_FILTER_KEYS.statusCode,
  SEARCH_BY_FILTER_KEYS.statusHistory,
  SEARCH_BY_FILTER_KEYS.statusHistoryDate,
  SEARCH_BY_FILTER_KEYS.tool,
  SEARCH_BY_FILTER_KEYS.type,
  SEARCH_BY_FILTER_KEYS.updateDate,
  SEARCH_BY_FILTER_KEYS.website
];

// --- GENERAL ASSETS KEYS ---

export const GENERAL_ASSETS_SEARCH_BY_FILTER_KEYS = [
  SEARCH_BY_FILTER_KEYS.creationTime,
  SEARCH_BY_FILTER_KEYS.assetCommand,
  SEARCH_BY_FILTER_KEYS.assetCreatorTool,
  SEARCH_BY_FILTER_KEYS.description,
  SEARCH_BY_FILTER_KEYS.hostNames,
  SEARCH_BY_FILTER_KEYS.id,
  SEARCH_BY_FILTER_KEYS.importance,
  SEARCH_BY_FILTER_KEYS.mac,
  SEARCH_BY_FILTER_KEYS.assetName,
  SEARCH_BY_FILTER_KEYS.openServices,
  SEARCH_BY_FILTER_KEYS.os,
  SEARCH_BY_FILTER_KEYS.owned,
  SEARCH_BY_FILTER_KEYS.assetOwner,
  SEARCH_BY_FILTER_KEYS.parameters,
  SEARCH_BY_FILTER_KEYS.totalRiskScore,
  SEARCH_BY_FILTER_KEYS.servicesName,
  SEARCH_BY_FILTER_KEYS.servicesPort,
  SEARCH_BY_FILTER_KEYS.servicesStatus,
  SEARCH_BY_FILTER_KEYS.servicesVersion,
  SEARCH_BY_FILTER_KEYS.tags__name,
  SEARCH_BY_FILTER_KEYS.lastModified,
  SEARCH_BY_FILTER_KEYS.vulnerabilities,
  SEARCH_BY_FILTER_KEYS.workspaceName
];

// --- GENERAL VULNS KEYS ---

export const GENERAL_VULNS_SEARCH_BY_FILTER_KEYS = [
  SEARCH_BY_FILTER_KEYS.asset,
  SEARCH_BY_FILTER_KEYS.assetOs,
  SEARCH_BY_FILTER_KEYS.confirmed,
  SEARCH_BY_FILTER_KEYS.createDate,
  SEARCH_BY_FILTER_KEYS.creatorCommandId,
  SEARCH_BY_FILTER_KEYS.cve_instances,
  SEARCH_BY_FILTER_KEYS.cvss2,
  SEARCH_BY_FILTER_KEYS.cvss3,
  SEARCH_BY_FILTER_KEYS.cvss4,
  SEARCH_BY_FILTER_KEYS.cwe,
  SEARCH_BY_FILTER_KEYS.data,
  SEARCH_BY_FILTER_KEYS.description,
  SEARCH_BY_FILTER_KEYS.easeOfResolution,
  SEARCH_BY_FILTER_KEYS.externalId,
  SEARCH_BY_FILTER_KEYS.id,
  SEARCH_BY_FILTER_KEYS.impact,
  SEARCH_BY_FILTER_KEYS.issueTracker,
  SEARCH_BY_FILTER_KEYS.lastDetected,
  SEARCH_BY_FILTER_KEYS.method,
  SEARCH_BY_FILTER_KEYS.name,
  SEARCH_BY_FILTER_KEYS.path,
  SEARCH_BY_FILTER_KEYS.query,
  SEARCH_BY_FILTER_KEYS.request,
  SEARCH_BY_FILTER_KEYS.resolution,
  SEARCH_BY_FILTER_KEYS.response,
  SEARCH_BY_FILTER_KEYS.risk_score,
  SEARCH_BY_FILTER_KEYS.serviceName,
  SEARCH_BY_FILTER_KEYS.servicePort,
  SEARCH_BY_FILTER_KEYS.severity,
  SEARCH_BY_FILTER_KEYS.status,
  SEARCH_BY_FILTER_KEYS.statusCode,
  SEARCH_BY_FILTER_KEYS.statusHistory,
  SEARCH_BY_FILTER_KEYS.statusHistoryDate,
  SEARCH_BY_FILTER_KEYS.tag_instances,
  SEARCH_BY_FILTER_KEYS.tool,
  SEARCH_BY_FILTER_KEYS.type,
  SEARCH_BY_FILTER_KEYS.updateDate,
  SEARCH_BY_FILTER_KEYS.website,
  SEARCH_BY_FILTER_KEYS.workspaceName
];

// --- WORKSPACES KEYS ---

export const WORKSPACES_SEARCH_BY_FILTER_KEYS = [
  SEARCH_BY_FILTER_KEYS.customer,
  SEARCH_BY_FILTER_KEYS.description,
  SEARCH_BY_FILTER_KEYS.endDate,
  SEARCH_BY_FILTER_KEYS.importance,
  SEARCH_BY_FILTER_KEYS.name,
  SEARCH_BY_FILTER_KEYS.permission,
  SEARCH_BY_FILTER_KEYS.public,
  SEARCH_BY_FILTER_KEYS.scopeInWs,
  SEARCH_BY_FILTER_KEYS.startDate
];

// --- SERVICES KEYS ---

export const SERVICES_SEARCH_BY_FILTER_KEYS = [
  SEARCH_BY_FILTER_KEYS.description,
  SEARCH_BY_FILTER_KEYS.host,
  SEARCH_BY_FILTER_KEYS.name,
  SEARCH_BY_FILTER_KEYS.owned,
  SEARCH_BY_FILTER_KEYS.port,
  SEARCH_BY_FILTER_KEYS.protocol,
  SEARCH_BY_FILTER_KEYS.statusInServices,
  SEARCH_BY_FILTER_KEYS.tag_instances,
  SEARCH_BY_FILTER_KEYS.version,
  SEARCH_BY_FILTER_KEYS.vulnerabilities
];

// --- SERVICES IN ASSET KEYS ---

export const SERVICES_ASSET_SEARCH_BY_FILTER_KEYS = [
  SEARCH_BY_FILTER_KEYS.description,
  SEARCH_BY_FILTER_KEYS.name,
  SEARCH_BY_FILTER_KEYS.owned,
  SEARCH_BY_FILTER_KEYS.port,
  SEARCH_BY_FILTER_KEYS.protocol,
  SEARCH_BY_FILTER_KEYS.statusInServices,
  SEARCH_BY_FILTER_KEYS.tag_instances,
  SEARCH_BY_FILTER_KEYS.version,
  SEARCH_BY_FILTER_KEYS.vulnerabilities
];

// --- GENERAL SERVICES KEYS ---

export const GENERAL_SERVICES_SEARCH_BY_FILTER_KEYS = [
  SEARCH_BY_FILTER_KEYS.description,
  SEARCH_BY_FILTER_KEYS.host,
  SEARCH_BY_FILTER_KEYS.name,
  SEARCH_BY_FILTER_KEYS.owned,
  SEARCH_BY_FILTER_KEYS.port,
  SEARCH_BY_FILTER_KEYS.protocol,
  SEARCH_BY_FILTER_KEYS.statusInServices,
  SEARCH_BY_FILTER_KEYS.version,
  SEARCH_BY_FILTER_KEYS.tag_instances,
  SEARCH_BY_FILTER_KEYS.vulnerabilities,
  SEARCH_BY_FILTER_KEYS.workspaceName
];

// --- REPORTS KEYS ---

export const REPORTS_SEARCH_BY_FILTER_KEYS = [
  SEARCH_BY_FILTER_KEYS.grouped,
  SEARCH_BY_FILTER_KEYS.name,
  SEARCH_BY_FILTER_KEYS.template_name,
  SEARCH_BY_FILTER_KEYS.conclusions,
  SEARCH_BY_FILTER_KEYS.objectives,
  SEARCH_BY_FILTER_KEYS.recommendations,
  SEARCH_BY_FILTER_KEYS.scope,
  SEARCH_BY_FILTER_KEYS.summary,
  SEARCH_BY_FILTER_KEYS.title,
  SEARCH_BY_FILTER_KEYS.confirmed,
  SEARCH_BY_FILTER_KEYS.vuln_count
];

// --- SCHEDULE KEYS ---

export const SCHEDULE_SEARCH_BY_FILTER_KEYS = [
  SEARCH_BY_FILTER_KEYS.createDate,
  SEARCH_BY_FILTER_KEYS.description,
  SEARCH_BY_FILTER_KEYS.crontab,
  SEARCH_BY_FILTER_KEYS.lastRun,
  SEARCH_BY_FILTER_KEYS.executorName,
  SEARCH_BY_FILTER_KEYS.active
];

// --- CREDENTIALS KEYS ---

export const CREDENTIALS_SEARCH_BY_FILTER_KEYS = [
  SEARCH_BY_FILTER_KEYS.clientEndpoint,
  SEARCH_BY_FILTER_KEYS.username,
  SEARCH_BY_FILTER_KEYS.linkedVulnerability,
  SEARCH_BY_FILTER_KEYS.leakDate,
  SEARCH_BY_FILTER_KEYS.owned,
  SEARCH_BY_FILTER_KEYS.password
];

// --- CLOUD SCHEDULE KEYS ---

export const CLOUD_SCHEDULE_SEARCH_BY_FILTER_KEYS = [
  SEARCH_BY_FILTER_KEYS.createDate,
  SEARCH_BY_FILTER_KEYS.description,
  SEARCH_BY_FILTER_KEYS.crontab,
  SEARCH_BY_FILTER_KEYS.lastRun,
  SEARCH_BY_FILTER_KEYS.cloudAgentName
];

// --- ANALYTICS KEYS ---

export const ANALYTICS_SEARCH_BY_FILTER_KEYS = [
  SEARCH_BY_FILTER_KEYS.createDate,
  SEARCH_BY_FILTER_KEYS.description,
  SEARCH_BY_FILTER_KEYS.graphType,
  SEARCH_BY_FILTER_KEYS.name,
  SEARCH_BY_FILTER_KEYS.updateDate
];

// --- NOTIFICATIONS KEYS ---

export const NOTIFICATIONS_SEARCH_BY_FILTER_KEYS = [
  SEARCH_BY_FILTER_KEYS.message,
  SEARCH_BY_FILTER_KEYS.notificationType,
  SEARCH_BY_FILTER_KEYS.triggered_by__username,
  SEARCH_BY_FILTER_KEYS.time
];

// --- TEMPLATES KEYS ---

export const TEMPLATES_SEARCH_BY_FILTER_KEYS = [
  SEARCH_BY_FILTER_KEYS.name
];

// --- STYLES KEYS ---

export const STYLES_SEARCH_BY_FILTER_KEYS = [
  SEARCH_BY_FILTER_KEYS.name
];

// --- AGENTS KEYS ---

export const RUNNERS_SEARCH_BY_FILTER_KEYS = [
  SEARCH_BY_FILTER_KEYS.name,
  SEARCH_BY_FILTER_KEYS.agentStatus,
  SEARCH_BY_FILTER_KEYS.lastExecutionDate,
  SEARCH_BY_FILTER_KEYS.lastExecutionTool,
  SEARCH_BY_FILTER_KEYS.blocked,
  SEARCH_BY_FILTER_KEYS.description,
  SEARCH_BY_FILTER_KEYS.tools,
  SEARCH_BY_FILTER_KEYS.toolCategory
];

// --- CLOUD AGENTS KEYS ---

export const SECURITY_SCANNERS_SEARCH_BY_FILTER_KEYS = [
  SEARCH_BY_FILTER_KEYS.name,
  SEARCH_BY_FILTER_KEYS.lastExecutionDate,
  SEARCH_BY_FILTER_KEYS.description,
  SEARCH_BY_FILTER_KEYS.category
];

// --- STATUS LOG KEYS ---

export const STATUS_LOG_SEARCH_BY_FILTER_KEYS = [
  SEARCH_BY_FILTER_KEYS.name,
  SEARCH_BY_FILTER_KEYS.triggeredByAgent,
  SEARCH_BY_FILTER_KEYS.workspacesStatusLog
];

export const CONFIRMED_FLAG_FILTERS = {
  confirmed: {
    name: 'confirmed',
    op: eq.name.value,
    val: 'true'
  },
  notConfirmed: {
    name: 'confirmed',
    op: eq.name.value,
    val: 'false'
  }
};

export const CLOSED_FILTERS = {
  closed: {
    name: 'status',
    op: neq.name.value,
    val: 'closed'
  },
  // "Not Closed" as used by the Dashboard toggle: status IN (open, re-opened).
  // Unlike `closed` (status != closed), this excludes risk-accepted too — it
  // stays a separate, visible metric, not part of the accountable backlog.
  notClosed: {
    name: 'status',
    op: FILTER_OPERATORS.in.name.value,
    val: ['open', 're-opened']
  }
};

const vulnsByRiskAvg = (op, value) => ({
  name: 'risk',
  op,
  val: value
});

const kbByName = (name) => ({
  name: 'name',
  op: ilike.name.value,
  val: name
});

export const KB_FILTERS = {
  kbByName
};

export const DASHBOARD_FILTERS = {
  commandIdFilter,
  vulnerabilitiesBySeverity,
  vulnerabilitiesByStatus,
  vulnerabilityById,
  assetsByIp,
  assetsByServiceName,
  vulnerabilitiesByType,
  vulnsByTag,
  assetsById,
  vulnsByRiskAvg
};

export const KEY_LABELS_MAP = Object.entries(SEARCH_BY_FILTER_KEYS).map((key) => key[1].name);
export const OPERATOR_LABELS_MAP = [
  ...Object.entries(FILTER_OPERATORS).map((operator) => operator[1].name),
  ...IMPACT_AUTOCOMPLETE.map((impactType) => impactType.name)
];
export const AUTOCOMPLETE_LABELS_MAP = Object.entries(SEARCH_BY_AUTOCOMPLETE_KEYS).map((autocomplete) => autocomplete[1].name);

export const COMMANDS_FILTER = (id) => ({
  name: 'commands',
  op: any.name.value,
  val: {
    name: 'id',
    op: '==',
    val: id
  }
});

export const PARSED_STATS = {
  hosts: 'hosts',
  hosts_confirmed: 'host_confirmed',
  hosts_notclosed_confirmed: 'host_notclosed_confirmed',
  hosts_notclosed: 'host_notclosed',
  services: 'services',
  services_confirmed: 'service_confirmed',
  services_notclosed_confirmed: 'service_notclosed_confirmed',
  services_notclosed: 'service_notclosed',
  vulns: 'total_vulns',
  vulns_confirmed: 'confirmed_vulns',
  vulns_notclosed_confirmed: 'notclosed_confirmed_vulns',
  vulns_notclosed: 'notclosed_vulns'
};
